import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { useUser } from "@/contexts/UserContext";
import { API_BASE_URL } from "@/services/api";
import type { SubscriptionStatusPayload } from "@/services/subscription-status";

/**
 * Warn-but-don't-block notice shown above the app whenever the company's trial
 * or paid period has ended. It links straight to /pay — the payment page —
 * because "make payment" is the only action the message asks for, and only
 * company admins can actually pay.
 *
 * Refetches on every route change so the banner clears as soon as the admin
 * comes back from /pay having paid.
 */
export function SubscriptionBanner() {
  const { isAdmin, loading } = useUser();
  const location = useLocation();
  const [subscription, setSubscription] = useState<SubscriptionStatusPayload | null>(null);

  useEffect(() => {
    if (loading || !isAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/v1/mpesa/subscription`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
          },
        });
        if (!cancelled && response.ok) setSubscription(await response.json());
      } catch {
        /* the banner is advisory — never block the app on it */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loading, isAdmin, location.pathname]);

  if (loading || !isAdmin || !subscription) return null;
  if (subscription.status !== "expired") return null;

  const overdue = subscription.days_overdue ?? 0;

  return (
    <div
      role="alert"
      data-testid="subscription-banner"
      className="border-b border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100"
    >
      <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
        <span className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
          <strong>Payment required.</strong> Your subscription expired{" "}
          {overdue === 0 ? "today" : `${overdue} day${overdue === 1 ? "" : "s"} ago`}.
        </span>
        <Link
          to="/pay"
          className="rounded-md bg-amber-600 px-3 py-1 font-medium text-white hover:bg-amber-700"
        >
          Make payment
        </Link>
      </div>
    </div>
  );
}
